import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const feteDuJasminGrasse: ChauffeurPage = {
  slug: "fete-du-jasmin-grasse",
  title: "Chauffeur privé Fête du Jasmin Grasse – VTC & navettes",
  subtitle:
    "Chauffeur privé pendant la Fête du Jasmin à Grasse : accès au corso fleuri, navettes depuis la côte et l'aéroport de Nice, stationnement évité dans la vieille ville.",
  keywords: [
    "chauffeur fête du jasmin grasse",
    "vtc fête du jasmin",
    "navette corso fleuri grasse",
  ],
  hero: {
    title: "Chauffeur privé pendant la Fête du Jasmin à Grasse",
    subtitle:
      "Corso fleuri début août dans la capitale du parfum : un chauffeur dédié quand les rues de Grasse se ferment et que le stationnement manque.",
    keywords: [
      "chauffeur Fête du Jasmin",
      "VTC Grasse",
      "navette corso fleuri",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant la Fête du Jasmin à Grasse",
    badges: [
      "Navettes depuis la côte",
      "Dépose vieille ville",
      "Retours de soirée",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant la Fête du Jasmin",
    items: [
      {
        title: "Vieille ville et rues fermées",
        description:
          "Le corso fleuri traverse le centre de Grasse : rues barrées et vieille ville piétonne. Le chauffeur vous dépose au plus près des accès autorisés.",
      },
      {
        title: "Stationnement de Grasse évité",
        description:
          "Perchée et dense, Grasse offre peu de places, encore moins pendant la fête. Dépose et reprise sans chercher à se garer.",
      },
      {
        title: "Navettes depuis la côte",
        description:
          "Depuis Cannes, Nice ou l'aéroport, liaisons directes vers Grasse pour le week-end du jasmin, sans souci de route ni de parking.",
      },
      {
        title: "Retours de soirée assurés",
        description:
          "Défilé, animations et dîners : le chauffeur patiente et assure le retour vers votre hôtel ou votre villa.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant la Fête du Jasmin",
    services: [
      {
        title: "Navettes depuis la côte",
        text: "Liaisons Cannes, Nice ou aéroport ↔ Grasse pour la Fête du Jasmin.",
      },
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert avec suivi de vol vers Grasse et son arrière-pays.",
      },
      {
        title: "Dépose au plus près du corso",
        text: "Approche des accès autorisés selon les rues fermées pour le défilé fleuri.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour combiner fête, visites de parfumeries et déjeuners.",
      },
      {
        title: "Soirées & dîners",
        text: "Restaurants de Grasse et de l'arrière-pays, retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant la Fête du Jasmin",
    items: [
      {
        name: "Vieille ville de Grasse",
        description:
          "Ruelles et places du centre historique, parcours du corso fleuri.",
      },
      {
        name: "Cours Honoré Cresp & parfumeries",
        description: "Cœur de la capitale du parfum et maisons emblématiques.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, porte d'entrée depuis l'aéroport.",
      },
      {
        name: "Cannes & littoral",
        description: "Liaisons depuis la côte vers Grasse pour la journée.",
      },
      {
        name: "Arrière-pays grassois",
        description: "Villas, domaines et restaurants autour de Grasse.",
      },
    ],
  },
  generic: {
    heading: "La Fête du Jasmin vue d'un chauffeur",
    subheading:
      "Début août, Grasse célèbre le jasmin par un corso fleuri qui traverse et referme la vieille ville.",
    paragraphs: [
      "Chaque début août, la Fête du Jasmin anime Grasse, capitale du parfum : corso fleuri, chars ornés de fleurs, musique et jasmin distribué au public. Le défilé traverse la vieille ville et ferme plusieurs rues ; un chauffeur qui connaît les accès autorisés vous rapproche sans vous perdre dans les ruelles.",
      "Grasse est perchée et son stationnement, déjà rare, sature pendant la fête. Depuis Cannes, Nice ou l'aéroport de Nice Côte d'Azur, le chauffeur assure la liaison et vous dépose au plus près, puis vous reprend à la sortie, sans recherche de place.",
      "Entre le défilé, les animations, les visites de parfumeries et les dîners, la mise à disposition garde le même chauffeur sur la journée et assure les retours de soirée vers votre hôtel ou votre villa.",
    ],
    bullets: [
      "Dépose au plus près du corso",
      "Navettes depuis la côte",
      "Stationnement de Grasse évité",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour la Fête du Jasmin",
    steps: [
      "Indiquez votre point de départ (côte, aéroport, hôtel) et vos horaires pour le week-end.",
      "Nous calons la liaison vers Grasse selon les rues fermées pour le corso fleuri.",
      "Le chauffeur vous dépose au plus près des accès et vous reprend à la sortie.",
      "Mise à disposition possible pour combiner fête, parfumeries et dîners.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pour la Fête du Jasmin à Grasse ?",
      a: "Réservez à l'avance : début août, Grasse est très fréquentée pour le corso fleuri. Indiquez votre point de départ, vos horaires et votre hôtel pour un chauffeur dédié sur le week-end.",
    },
    {
      q: "Pouvez-vous déposer au plus près du corso fleuri ?",
      a: "Oui. Le défilé ferme plusieurs rues de la vieille ville. Le chauffeur vous dépose au point autorisé le plus proche, puis vous reprend à la sortie, sans que vous ayez à vous garer.",
    },
    {
      q: "Proposez-vous des navettes depuis la côte ?",
      a: "Oui. Depuis Cannes, Nice ou l'aéroport de Nice Côte d'Azur, nous assurons des liaisons directes vers Grasse pour la Fête du Jasmin.",
    },
    {
      q: "Le stationnement est-il difficile à Grasse pendant la fête ?",
      a: "Oui. Grasse est perchée et ses places sont rares, encore plus pendant le corso. Le chauffeur vous évite la recherche de stationnement : dépose et reprise aux accès autorisés.",
    },
    {
      q: "Peut-on combiner la fête avec la visite de parfumeries ?",
      a: "Oui. En mise à disposition, le chauffeur enchaîne défilé, visites des maisons de parfum et déjeuners, à votre rythme.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour la Fête du Jasmin ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : ponctualité et connaissance de Grasse et de son arrière-pays.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur privé Grasse", href: "/chauffeur/grasse" },
    { label: "VTC à Cannes", href: "/chauffeur/cannes" },
    { label: "Chauffeur aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "Location de voiture à Grasse",
      href: "/location/location-voiture-grasse",
    },
  ],
  cta: {
    title: "Votre chauffeur pour la Fête du Jasmin",
    text: "Navettes depuis la côte, dépose au corso et retours de soirée : réservez un chauffeur dédié pour Grasse.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur Fête du Jasmin Grasse — VTC & navettes",
    description:
      "Chauffeur privé pendant la Fête du Jasmin à Grasse : navettes depuis la côte et l'aéroport de Nice, dépose au corso fleuri, stationnement évité. Réservez.",
    canonical: "/chauffeur/fete-du-jasmin-grasse",
  },
};

export default feteDuJasminGrasse;
